import heroVideo from 'assets/dreamsbuilt.webm';
import posterImg from 'assets/poster.webp';
import { useEffect, useState, type FC } from 'react';
import { COMPANY } from '../content';
import { DimensionLine, GhostLink, PrimaryLink } from '../primitives';

/**
 * Full-height hero. The prerendered HTML always contains the poster image
 * (deterministic first paint, best LCP); the looping site video swaps in
 * after mount on wider screens.
 */
export const Hero: FC = () => {
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    const wideEnough = window.matchMedia('(min-width: 768px)').matches;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (wideEnough && !reducedMotion) setShowVideo(true);
  }, []);

  return (
    <section className="relative flex min-h-[100svh] items-center overflow-hidden bg-ink-900">
      <img
        src={posterImg}
        alt="Concrete foundation pour in progress on a Waikato residential site"
        className="absolute inset-0 h-full w-full object-cover"
        fetchPriority="high"
      />
      {showVideo ? (
        <video
          className="absolute inset-0 h-full w-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          poster={posterImg}
        >
          <source src={heroVideo} type="video/webm" />
        </video>
      ) : null}
      <div className="absolute inset-0 bg-gradient-to-r from-ink-900/90 via-ink-900/60 to-ink-900/30" />

      <div className="relative mx-auto w-full max-w-6xl px-5 pt-24 pb-16 sm:px-8">
        <p className="font-mono text-xs tracking-[0.25em] text-brand-400 uppercase sm:text-sm">
          Concrete foundations - Hamilton &amp; Waikato
        </p>
        <h1 className="font-display mt-5 max-w-3xl text-4xl leading-[1.05] font-bold text-white sm:text-6xl">
          The floors under Waikato&rsquo;s new homes.
        </h1>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-white/80 sm:text-lg">
          {COMPANY.name} pours residential foundations, driveways and patios for group builders and
          homeowners across the Waikato: flat, square, and on the day we said.
        </p>

        <div className="mt-9 flex flex-col gap-3 sm:flex-row">
          <PrimaryLink to="/quote">Get a free quote</PrimaryLink>
          <GhostLink to="/projects" dark>
            See our work
          </GhostLink>
        </div>

        <DimensionLine
          label={`600+ floors poured since ${COMPANY.foundingYear}`}
          className="mt-14 max-w-xl"
        />
      </div>
    </section>
  );
};
